import React from 'react';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { useExposureData } from './useExposureData';
import { useExposureActions } from './useExposureActions';
import StudentListTable from './StudentListTable';
import EditExposureDialog from './EditExposureDialog';
import AddStudentDialog from './AddStudentDialog';
import RemoveStudentDialog from './RemoveStudentDialog';
import { REMOVE_REASON_LIST } from './constants';
import './student-exposure-visit.scss';

const StudentExposureVisit: React.FC = () => {
  const toastRef = useRef<Toast>(null);
  const data = useExposureData();
  const a = useExposureActions(data, toastRef);

  return (
    <div className="student-exposure-visit">
      <Toast ref={toastRef} />

      <div className="breadcrumbs c-black">
        <h6 className="url d-flex align-items-center gap-2 m-0">
          <b>Students</b>
          <i className="pi pi-angle-right" />
          <span>Student List</span>
        </h6>
      </div>

      {/* Upload section */}
      <div className="card mt-3">
        <div className="card-body">
          <h6 className="fw-bold mb-3 border-bottom pb-2">
            Upload Photo &amp; Video
          </h6>
          <div className="row g-3">
            <div className="col-12 col-md-5 col-lg-4">
              <label className="form-label inMandatory fw-bold">
                Exposure Visit Student Group Photo
              </label>
              <div className="d-flex align-items-center">
                <input
                  key={`photo-${a.photoResetKey}`}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  className="form-control form-control-sm me-2"
                  disabled={a.isImageUploading || a.isUploadLocked}
                  onChange={(e) =>
                    a.onFileSelect('image', e.target.files?.[0] ?? null)
                  }
                />
                {a.imageKey && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger me-2"
                    title="Clear"
                    disabled={a.isImageUploading}
                    onClick={() => a.onClearFile('image')}
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
                {a.imageKey && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2"
                    title="View"
                    onClick={() => a.onViewFile('image')}
                  >
                    <i className="pi pi-eye" />
                  </button>
                )}
              </div>
              {a.imageKey && a.imageName && (
                <span className="form-text text-success small d-block">
                  <i className="pi pi-check-circle me-1" />
                  {a.imageName}
                </span>
              )}
              {a.isImageUploading && (
                <span className="form-text text-primary small">Uploading…</span>
              )}
              {a.uploadSubmitted && !a.imageKey && (
                <span className="form-text text-danger small">
                  Field is required
                </span>
              )}
              <div className="form-text small text-muted">
                PNG / JPG / JPEG only, max 15 MB.
              </div>
            </div>
            <div className="col-12 col-md-5 col-lg-4">
              <label className="form-label inMandatory fw-bold">
                Testimonial Video
              </label>
              <div className="d-flex align-items-center">
                <input
                  key={`video-${a.videoResetKey}`}
                  type="file"
                  accept=".mp4,video/mp4"
                  className="form-control form-control-sm me-2"
                  disabled={a.isVideoUploading || a.isUploadLocked}
                  onChange={(e) =>
                    a.onFileSelect('video', e.target.files?.[0] ?? null)
                  }
                />
                {a.videoKey && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger me-2"
                    title="Clear"
                    disabled={a.isVideoUploading}
                    onClick={() => a.onClearFile('video')}
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
                {a.videoKey && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2"
                    title="View"
                    onClick={() => a.onViewFile('video')}
                  >
                    <i className="pi pi-eye" />
                  </button>
                )}
              </div>
              {a.videoKey && a.videoName && (
                <span className="form-text text-success small d-block">
                  <i className="pi pi-check-circle me-1" />
                  {a.videoName}
                </span>
              )}
              {a.isVideoUploading && (
                <span className="form-text text-primary small">Uploading…</span>
              )}
              {a.uploadSubmitted && !a.videoKey && (
                <span className="form-text text-danger small">
                  Field is required
                </span>
              )}
              <div className="form-text small text-muted">
                MP4 only, max 15 MB.
              </div>
            </div>
          </div>
          {!a.isUploadLocked && (
            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={a.isImageUploading || a.isVideoUploading}
                onClick={a.onUploadSubmit}
              >
                Submit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table shown only after a successful upload submit */}
      {/* {a.isSubmitted && ( */}
      <div className="mt-3">
        <StudentListTable
          students={data.studentJsonData}
          onAddStudent={a.addStudent}
          onEdit={a.onRowEdit}
          onDelete={a.confirmDelete}
        />
      </div>
      {/* )} */}

      <EditExposureDialog
        teacherOptions={data.teacherOptions}
        visible={a.displayEditDialog}
        title={a.editDialogTitle}
        form={a.form}
        submitted={a.submitted}
        collegeMaster={data.collegeMaster}
        districtList={data.districtList}
        onHide={() => a.setDisplayEditDialog(false)}
        setField={a.setField}
        onCollegeChange={a.handleCollegeChange}
        onDistrictChange={a.handleDistrictChange}
        onReasonChange={a.handleReasonChange}
        onVisitChange={a.handleVisitChange}
        onSubmit={a.onSubmit}
      />

      <AddStudentDialog
        visible={a.displayDialog}
        onHide={() => a.setDisplayDialog(false)}
        selectedClass={a.selectedClass}
        selectedSection={a.selectedSection}
        sectionListArr={a.sectionListArr}
        onSectionChange={a.onSectionChange}
        search={a.dialogSearch}
        onSearchChange={a.setDialogSearch}
        students={a.dialogStudentList}
        selectedStudents={a.selectedStudents}
        onSelectionChange={a.setSelectedStudents}
        isAdding={data.isAddingStudents}
        onAddSelected={a.onAddSelected}
      />

      <RemoveStudentDialog
        visible={!!a.removeRow}
        reasonList={REMOVE_REASON_LIST}
        reason={a.removeReason}
        otherReason={a.removeOther}
        submitted={a.removeSubmitted}
        isRemoving={a.isRemoving}
        onReasonChange={a.onRemoveReasonChange}
        onOtherChange={a.setRemoveOther}
        onHide={a.closeRemove}
        onConfirm={a.onRemoveConfirm}
      />
    </div>
  );
};

export default StudentExposureVisit;
